import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import LanguageSwitcher from '@/components/feature/LanguageSwitcher';

interface SubMenuItem {
  label: string;
  href: string;
  desc?: string;
}

interface NavItem {
  label: string;
  href: string;
  children?: SubMenuItem[];
}

const navItems: NavItem[] = [
  { label: '首页', href: '/' },
  {
    label: 'MossTeams',
    href: '/mossteams',
    children: [
      { label: '智能会议双屏机', href: '/mossteams/dual-screen', desc: '会议室专属AI秘书' },
      { label: '桌面客户端', href: '/mossteams/desktop-client', desc: '会议一体机桌面搭档' },
      { label: '离线语音盒子', href: '/mossteams/offline-voice-box', desc: '离线AI语音算力核心' },
    ],
  },
  {
    label: 'MossTalk',
    href: '/mosstalk',
    children: [
      { label: '标准款翻译机', href: '/mosstalk/standard', desc: '指环式随身翻译官' },
      { label: '商务款翻译机', href: '/mosstalk/business', desc: '商务专属AI翻译' },
      { label: '翻译耳机', href: '/mosstalk/earbuds', desc: '降噪翻译耳机' },
    ],
  },
  {
    label: 'MossVox',
    href: '/mossvox',
    children: [
      { label: '字幕精灵 Pro', href: '/mossvox/subtitle-pro', desc: '智能字幕助手' },
      { label: '录音牌', href: '/mossvox/recorder', desc: '高清录音硬件' },
    ],
  },
  {
    label: '更多产品',
    href: '/more-products',
    children: [
      { label: '智译一体机', href: '/more-products/all-in-one', desc: '私有化 AI 翻译工作站' },
    ],
  },
  {
    label: '服务支持',
    href: '/support',
    children: [
      { label: '客户案例', href: '/support/cases', desc: '政企/高校/外贸实证' },
      { label: '资源下载', href: '/support/downloads', desc: '白皮书·说明书' },
    ],
  },
  { label: '联系我们', href: '/contact' },
];

function Navbar() {
  // 顶部样式固定为滚动后的样式，不随滚动变化
  const scrolled = true;
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [mobileExpanded, setMobileExpanded] = useState<Set<string>>(new Set());
  const location = useLocation();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMouseEnter = (label: string) => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setActiveDropdown(label);
  };

  const handleMouseLeave = () => {
    closeTimer.current = setTimeout(() => {
      setActiveDropdown(null);
    }, 200);
  };

  const isActive = (href: string) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  const toggleMobileExpand = (label: string) => {
    setMobileExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }
      return next;
    });
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white/95 backdrop-blur-md border-b border-background-200`}
    >
      <div className="flex items-center justify-between h-16 md:h-18 px-4 md:px-8 lg:px-12">
        <Link to="/" className="flex items-center gap-2 flex-shrink-0">
          <div className={`w-8 h-8 md:w-9 md:h-9 rounded-lg flex items-center justify-center font-extrabold text-sm md:text-base transition-colors duration-300`}>
            <img src="/images/logo.png" alt="Logo" className="w-full h-full object-contain" />
          </div>
          <span className={`xykj-no-translate font-bold text-base md:text-lg tracking-tight whitespace-nowrap transition-colors duration-300 text-primary-600`}>
            新译科技·智能硬件
          </span>
        </Link>

        <div className="hidden lg:flex items-center gap-1" ref={dropdownRef}>
          {navItems.map((item) => (
            <div
              key={item.label}
              className="relative"
              onMouseEnter={() => item.children && handleMouseEnter(item.label)}
              onMouseLeave={() => item.children && handleMouseLeave()}
            >
              {item.children ? (
                <button
                  type="button"
                  onClick={() => setActiveDropdown((prev) => (prev === item.label ? null : item.label))}
                  className={`relative flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-md transition-all duration-200 whitespace-nowrap cursor-pointer
                    ${isActive(item.href)
                      ? (scrolled ? 'text-primary-600 bg-primary-50' : 'text-white bg-white/15')
                      : (scrolled ? 'text-foreground-700 hover:text-primary-600 hover:bg-primary-50/50' : 'text-white/85 hover:text-white hover:bg-white/10')
                    }`}
                >
                  {item.label}
                  <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${activeDropdown === item.label ? 'rotate-180' : ''}`}></i>
                </button>
              ) : (
                <Link
                  to={item.href}
                  className={`relative flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-md transition-all duration-200 whitespace-nowrap cursor-pointer
                    ${isActive(item.href)
                      ? (scrolled ? 'text-primary-600 bg-primary-50' : 'text-white bg-white/15')
                      : (scrolled ? 'text-foreground-700 hover:text-primary-600 hover:bg-primary-50/50' : 'text-white/85 hover:text-white hover:bg-white/10')
                    }`}
                >
                  {item.label}
                </Link>
              )}

              {item.children && activeDropdown === item.label && (
                <div
                  className="absolute top-full left-1/2 -translate-x-1/2 pt-2"
                  onMouseEnter={() => handleMouseEnter(item.label)}
                  onMouseLeave={() => handleMouseLeave()}
                >
                  <div className="bg-white rounded-xl border border-background-200 py-2 px-1 min-w-[200px]"
                    style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}
                  >
                    {item.children.map((child) => (
                      <Link
                        key={child.href}
                        to={child.href}
                        className="flex flex-col px-4 py-2.5 rounded-lg hover:bg-primary-50 transition-colors duration-150 cursor-pointer"
                        onClick={() => setActiveDropdown(null)}
                      >
                        <span className="text-sm font-medium text-foreground-800">{child.label}</span>
                        {child.desc && (
                          <span className="text-xs text-foreground-500 mt-0.5">{child.desc}</span>
                        )}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="flex items-center gap-2 md:gap-3 flex-shrink-0">
          <LanguageSwitcher scrolled={scrolled} />

          <div className="hidden lg:flex items-center gap-3">
            <a
              href="#consultation"
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer
                ${scrolled
                  ? 'bg-primary-500 text-white hover:bg-primary-600'
                  : 'bg-white/20 text-white hover:bg-white/30 backdrop-blur-sm'
                }`}
            >
              <i className="ri-customer-service-2-line text-base"></i>
              <span>在线咨询</span>
            </a>
            <span className={`text-sm font-semibold whitespace-nowrap transition-colors duration-200 ${scrolled ? 'text-foreground-800' : 'text-white'}`}>
              <i className="ri-phone-line mr-1"></i>400-097-5066
            </span>
          </div>

          <button
            className="lg:hidden flex items-center justify-center w-10 h-10 rounded-lg cursor-pointer transition-colors duration-200"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="菜单"
          >
            <i className={`text-xl transition-colors duration-200 ${scrolled ? 'text-foreground-800' : 'text-white'} ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-background-200 max-h-[calc(100vh-4rem)] overflow-y-auto">
          <div className="px-4 py-3 space-y-1">
            {navItems.map((item) => (
              <div key={item.label}>
                {item.children ? (
                  <>
                    <button
                      className="w-full flex items-center justify-between px-3 py-3 text-sm font-medium text-foreground-800 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
                      onClick={() => toggleMobileExpand(item.label)}
                    >
                      {item.label}
                      <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileExpanded.has(item.label) ? 'rotate-180' : ''}`}></i>
                    </button>
                    {mobileExpanded.has(item.label) && (
                      <div className="ml-4 border-l-2 border-primary-100 pl-3 space-y-1 mt-1 mb-2">
                        {item.children.map((child) => (
                          <Link
                            key={child.href}
                            to={child.href}
                            className="block px-3 py-2.5 text-sm text-foreground-600 rounded-lg hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer"
                            onClick={() => setMobileOpen(false)}
                          >
                            <div>{child.label}</div>
                            {child.desc && <div className="text-xs text-foreground-400 mt-0.5">{child.desc}</div>}
                          </Link>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <Link
                    to={item.href}
                    className={`block px-3 py-3 text-sm font-medium rounded-lg transition-colors cursor-pointer
                      ${isActive(item.href) ? 'text-primary-600 bg-primary-50' : 'text-foreground-800 hover:bg-background-100'}`}
                    onClick={() => setMobileOpen(false)}
                  >
                    {item.label}
                  </Link>
                )}
              </div>
            ))}
          </div>
          <div className="px-4 py-4 border-t border-background-200 flex flex-col gap-3">
            <a
              href="#consultation"
              className="flex items-center justify-center gap-2 px-4 py-3 bg-primary-500 text-white text-sm font-medium rounded-full cursor-pointer"
              onClick={() => setMobileOpen(false)}
            >
              <i className="ri-customer-service-2-line"></i>在线咨询
            </a>
            <div className="flex items-center justify-between text-sm">
              <a href="/support/downloads" className="text-foreground-600 hover:text-primary-600 cursor-pointer" onClick={() => setMobileOpen(false)}>
                <i className="ri-download-2-line mr-1"></i>资料下载
              </a>
              <span className="text-foreground-800 font-semibold">
                <i className="ri-phone-line mr-1"></i>400-097-5066
              </span>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}

export default Navbar;